import { useState, useEffect } from "react";
import axios from "axios";
import { Save, Loader2, Info, Sparkles, Key, Cpu, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Toaster, toast } from "@/components/ui/sonner";
import PageHeader from "@/components/PageHeader";

const API = process.env.REACT_APP_BACKEND_URL;

const SCOPES = [
  {
    id: "generale",
    label: "Tono generale dello studio",
    icon: "🏥",
    placeholder: "Scrivi qui le regole generali sul tono e stile di ogni preventivo:\n\n• Formalità (Lei / tu)\n• Termini da usare o evitare\n• Riferimenti allo studio (es. \"BDD Studio Dentistico\")\n• Note su rateizzazione, pagamenti, orari\n• Firme, contatti, disclaimer legali",
    description: "Regole valide per tutti i preventivi (Ortodonzia + Reparto Clinico).",
  },
  {
    id: "ortodonzia",
    label: "Regole specifiche Ortodonzia",
    icon: "🦷",
    placeholder: "Scrivi qui le regole specifiche per i preventivi di ortodonzia:\n\n• Tipologie di trattamento (Invisalign, apparecchio fisso, mobile)\n• Terminologia da usare (es. \"apparecchio invisibile\" invece di \"Invisalign\")\n• Durata media e refinement\n• Controlli mensili inclusi/esclusi\n• Retention post-trattamento\n• Note su ATTM / bite / mantenitori",
    description: "Aggiunte SOLO se il piano è di reparto Ortodonzia.",
  },
  {
    id: "reparto_clinico",
    label: "Regole specifiche Reparto Clinico",
    icon: "⚕️",
    placeholder: "Scrivi qui le regole specifiche per i preventivi del reparto clinico:\n\n• Terminologia (es. \"impianto endosseo\" vs \"impianto\")\n• Protocolli chirurgici standard (guarigione, tempi)\n• Note su radiografie/TC incluse\n• Sedazione cosciente e anestesia\n• Follow-up e controlli\n• Note sulla protesi (temporanea/definitiva)",
    description: "Aggiunte SOLO se il piano è di reparto Clinico.",
  },
];

export default function IstruzioniAiPage() {
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  // LLM config panel
  const [llmConfig, setLlmConfig] = useState(null);
  const [llmSaving, setLlmSaving] = useState(false);
  const [customKeyInput, setCustomKeyInput] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [selectedModel, setSelectedModel] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [kbRes, llmRes] = await Promise.all([
        axios.get(`${API}/api/ai-knowledge`, { withCredentials: true }),
        axios.get(`${API}/api/ai-knowledge/llm-config`, { withCredentials: true }),
      ]);
      const map = {};
      (kbRes.data.items || []).forEach((i) => { map[i.scope] = i; });
      setItems(map);
      setLlmConfig(llmRes.data);
      setSelectedModel(llmRes.data.model);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const updateContent = (scope, content) => {
    setItems((m) => ({ ...m, [scope]: { ...(m[scope] || { scope }), content } }));
  };

  const saveScope = async (scope) => {
    const content = items[scope]?.content || "";
    setSaving((s) => ({ ...s, [scope]: true }));
    try {
      await axios.put(
        `${API}/api/ai-knowledge/${scope}`,
        { content },
        { withCredentials: true },
      );
      toast.success("Istruzioni salvate — saranno usate nei prossimi preventivi");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving((s) => ({ ...s, [scope]: false }));
    }
  };

  const saveLlmConfig = async ({ eraseKey = false } = {}) => {
    setLlmSaving(true);
    try {
      const payload = { model: selectedModel };
      if (eraseKey) {
        payload.anthropic_api_key = "";
      } else if (customKeyInput.trim()) {
        payload.anthropic_api_key = customKeyInput.trim();
      }
      // Se nessuna delle 2 condizioni, non passiamo anthropic_api_key → il
      // backend mantiene la chiave esistente e aggiorna solo il modello.
      await axios.put(
        `${API}/api/ai-knowledge/llm-config`,
        payload,
        { withCredentials: true },
      );
      toast.success(
        eraseKey
          ? "Chiave Anthropic rimossa — torno a Emergent LLM Key"
          : "Configurazione LLM salvata",
      );
      setCustomKeyInput("");
      setShowKey(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setLlmSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa] bg-grid-light pt-[calc(7px+3.5rem+13px)]" data-testid="istruzioni-ai-page">
      <Toaster position="top-right" theme="light" />
      <div className="max-w-5xl mx-auto px-4 lg:px-6 pb-8">
        <PageHeader
          title="Istruzioni Assistente AI"
          subtitle="Configurazione LLM e regole domain-specific per l'assistente Preventivi"
        />

        {/* LLM Configuration Panel */}
        <Card className="mt-6 border-slate-200 shadow-sm" data-testid="llm-config-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center flex-shrink-0">
                <Cpu className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800">Configurazione LLM (Claude / GPT)</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Scegli il modello e (facoltativo) inserisci la tua chiave API Anthropic personale. Se nessuna chiave custom è impostata, viene usata la chiave universale Emergent.
                </p>
              </div>
            </div>

            {llmConfig && (
              <>
                {/* Model selector */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Modello AI attivo (preventivi)</label>
                    <select
                      value={selectedModel}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-500"
                      data-testid="llm-model-select"
                    >
                      {llmConfig.supported_models?.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                    {(() => {
                      const entry = llmConfig.supported_models?.find((m) => m.id === selectedModel);
                      if (entry?.requires_custom_key && !llmConfig.has_custom_key && !customKeyInput.trim()) {
                        return (
                          <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                            ⚠ Questo modello richiede una chiave Anthropic personale (vedi sotto).
                          </p>
                        );
                      }
                      return (
                        <p className="text-xs text-slate-400 mt-1">
                          Attualmente: <span className="font-mono text-slate-600">{llmConfig.model}</span>
                        </p>
                      );
                    })()}
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block flex items-center gap-1.5">
                      <Key className="w-3.5 h-3.5" />
                      Chiave API Anthropic (opzionale)
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          type={showKey ? "text" : "password"}
                          value={customKeyInput}
                          onChange={(e) => setCustomKeyInput(e.target.value)}
                          placeholder={llmConfig.has_custom_key ? llmConfig.anthropic_api_key_masked : "sk-ant-api03-..."}
                          className="pr-9 font-mono text-xs"
                          data-testid="llm-anthropic-key-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowKey((v) => !v)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          aria-label={showKey ? "Nascondi chiave" : "Mostra chiave"}
                        >
                          {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      {llmConfig.has_custom_key ? (
                        <span className="text-emerald-700">
                          ✓ Chiave custom attiva ({llmConfig.anthropic_api_key_masked}). Lascia vuoto per mantenerla.
                        </span>
                      ) : (
                        <span>Vuoto = usa la chiave universale Emergent (paga Emergent).</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-100">
                  <div className="text-xs text-slate-400">
                    {llmConfig.updated_at && (
                      <>Ultima modifica: {new Date(llmConfig.updated_at).toLocaleString("it-IT")}
                      {llmConfig.updated_by_email ? ` da ${llmConfig.updated_by_email}` : ""}</>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {llmConfig.has_custom_key && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (window.confirm("Rimuovere la chiave Anthropic custom? Tornerai a usare la chiave Emergent universale.")) {
                            saveLlmConfig({ eraseKey: true });
                          }
                        }}
                        disabled={llmSaving}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid="llm-erase-key-btn"
                      >
                        Rimuovi chiave custom
                      </Button>
                    )}
                    <Button
                      onClick={() => saveLlmConfig()}
                      disabled={llmSaving}
                      className="bg-slate-900 hover:bg-slate-800 text-white"
                      data-testid="save-llm-config-btn"
                    >
                      {llmSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Salva configurazione
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="mt-6 border-violet-200 bg-violet-50/40" data-testid="how-it-works-card">
          <CardContent className="p-4 flex gap-3 text-sm text-violet-900">
            <Info className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold mb-1">Come funziona</p>
              <p className="text-violet-800/90">
                Quando un utente crea un preventivo AI, il sistema aggiunge queste istruzioni al prompt di GPT.
                Il testo <strong>Tono generale</strong> è sempre incluso; il testo <strong>Ortodonzia</strong> o
                <strong> Reparto Clinico</strong> viene aggiunto solo se il piano di cura appartiene a quel reparto.
                Modifiche attive immediatamente sul prossimo preventivo generato — nessun redeploy.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6 mt-6">
          {loading ? (
            <div className="flex items-center justify-center p-12 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
            </div>
          ) : (
            SCOPES.map((s) => {
              const item = items[s.id] || {};
              return (
                <Card key={s.id} className="border-slate-200 shadow-sm" data-testid={`kb-card-${s.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3 gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                          <span>{s.icon}</span>
                          {s.label}
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">{s.description}</p>
                        {item.updated_at && (
                          <p className="text-xs text-slate-400 mt-1">
                            Ultima modifica: {new Date(item.updated_at).toLocaleString("it-IT")}
                            {item.updated_by_email ? ` da ${item.updated_by_email}` : ""}
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={() => saveScope(s.id)}
                        disabled={saving[s.id]}
                        className="bg-violet-600 hover:bg-violet-700 text-white shrink-0"
                        data-testid={`save-${s.id}-btn`}
                      >
                        {saving[s.id] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Salva
                      </Button>
                    </div>
                    <Textarea
                      value={item.content || ""}
                      onChange={(e) => updateContent(s.id, e.target.value)}
                      placeholder={s.placeholder}
                      className="min-h-[220px] text-sm font-mono resize-y"
                      data-testid={`kb-textarea-${s.id}`}
                    />
                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Supporta markdown (**grassetto**, *corsivo*, - liste, ecc.)
                    </p>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
